import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AdminPage from "../components/admin/AdminPage";
import AdminSection from "../components/admin/AdminSection";
import PlayerHeadshot from "../components/PlayerHeadshot";
import { communityDebatePath } from "../utils/community";
import {
  useGetAdminCommunityDailyQuery,
  useGetAdminCommunityHistoryQuery,
  usePinAdminCommunityDailyMutation,
  usePreviewAdminCommunityDailyMutation,
  useRegenerateAdminCommunityDailyMutation,
  useDeleteAdminCommunityCommentMutation,
  useArchiveAdminPromptKeyMutation,
} from "../store/api/communityApi";

const operateErrorMessage = (err) => err?.data?.error || err?.message;

const firstOperateError = (...errs) =>
  operateErrorMessage(errs.find((candidate) => candidate));

const flattenComments = (nodes = []) =>
  nodes.flatMap((node) => [node, ...flattenComments(node.replies)]);

const AdminCommunity = () => {
  const [sport, setSport] = useState("mlb");
  const [question, setQuestion] = useState("");
  const [candidateText, setCandidateText] = useState("");
  const { data, error } = useGetAdminCommunityDailyQuery({ sport });
  const history = useGetAdminCommunityHistoryQuery(sport);
  const [pin, pinState] = usePinAdminCommunityDailyMutation();
  const [preview, previewState] = usePreviewAdminCommunityDailyMutation();
  const [regenerate, regenerateState] = useRegenerateAdminCommunityDailyMutation();
  const [softDelete, deleteState] = useDeleteAdminCommunityCommentMutation();
  const [archive, archiveState] = useArchiveAdminPromptKeyMutation();
  const debate = data?.debate;
  const insights = data?.insights;
  const comments = flattenComments(debate?.comments);

  return (
    <AdminPage
      title="Community"
      subtitle="Today's official debate"
      error={firstOperateError(
        error,
        history.error,
        pinState?.error,
        previewState?.error,
        regenerateState?.error,
        archiveState?.error,
        deleteState?.error
      )}
    >
      <FormControl size="small" sx={{ mb: 2, minWidth: 140 }}>
        <InputLabel>Sport</InputLabel>
        <Select
          label="Sport"
          value={sport}
          onChange={(event) => setSport(event.target.value)}
        >
          <MenuItem value="mlb">MLB</MenuItem>
          <MenuItem value="nba">NBA</MenuItem>
          <MenuItem value="nfl">NFL</MenuItem>
        </Select>
      </FormControl>

      <AdminSection title="Today">
        <Typography fontWeight={800}>{debate?.question || "No daily yet"}</Typography>
        <Typography color="text.secondary">source: {debate?.source || "—"}</Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {(debate?.candidates || []).map((candidate) => (
            <Box key={candidate.playerId}>
              <PlayerHeadshot playerId={candidate.playerId} sport={sport} size={64} />
              <Typography>{candidate.name}</Typography>
            </Box>
          ))}
        </Box>
        {insights && (
          <Typography sx={{ mt: 1 }}>
            {insights.voters} voters · {insights.split.join("/")} · {insights.flips}{" "}
            flips · {insights.topLevelComments} comments · {insights.replies} replies
          </Typography>
        )}
        <Button
          sx={{ mt: 1 }}
          onClick={() =>
            debate?.promptKey &&
            archive({ promptKey: debate.promptKey, sport })
          }
        >
          Archive this prompt key
        </Button>
        {comments.map((comment) => (
          <Box key={comment.id}>
            <Typography>{comment.deleted ? "removed" : comment.body}</Typography>
            {!comment.deleted && (
              <Button onClick={() => softDelete(comment.id)}>Delete</Button>
            )}
          </Box>
        ))}
      </AdminSection>

      <AdminSection
        title="Preview"
        action={
          <Button onClick={() => preview({ sport })}>Preview</Button>
        }
      >
        <Typography>{previewState.data?.preview?.question || "Run preview (no write)."}</Typography>
        <Button sx={{ mt: 1 }} onClick={() => regenerate({ sport })}>
          Regenerate
        </Button>
      </AdminSection>

      <AdminSection title="Pin / replace">
        <TextField
          label="Question"
          fullWidth
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          sx={{ mb: 1 }}
        />
        <TextField
          label="Candidate ids (2 or 4, comma-separated)"
          fullWidth
          value={candidateText}
          onChange={(event) => setCandidateText(event.target.value)}
        />
        <Button
          sx={{ mt: 1 }}
          onClick={() =>
            pin({
              sport,
              question,
              candidateIds: candidateText.split(",").map((id) => id.trim()).filter(Boolean),
            })
          }
        >
          Pin
        </Button>
      </AdminSection>

      <AdminSection title="History">
        {(history.data?.items || []).map((item) => (
          <Box key={item.date} sx={{ mb: 1 }}>
            <Typography>
              {item.date} · {item.source} · {item.question} · {item.insights?.voters}{" "}
              votes · {item.insights?.flips} flips
            </Typography>
            <Button
              component={RouterLink}
              to={communityDebatePath(sport, item.debateId)}
            >
              Open debate
            </Button>
          </Box>
        ))}
      </AdminSection>
    </AdminPage>
  );
};

export default AdminCommunity;
